import { useState, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { supabase } from '../../api/supabase'
import { useAuth } from '../../contexts/AuthContext'
import { hasAnyRole } from '../../lib/roles'

interface VatRate {
  id?: string
  code: string
  description: string
  rate: number
  is_active: boolean
}

const empty: VatRate = { code: '', description: '', rate: 0, is_active: true }
const inputCls = 'w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white'

function Field({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      {children}
    </div>
  )
}

function Modal({ title, onClose, onSubmit, saving, error, children }: {
  title: string; onClose: () => void; onSubmit: (e: React.FormEvent) => void
  saving: boolean; error: string | null; children: React.ReactNode
}) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-md">
        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-bold text-gray-900 dark:text-white">{title}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <form onSubmit={onSubmit} className="p-6 space-y-4">
          {error && <div className="p-3 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 rounded-lg text-sm">{error}</div>}
          {children}
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">Cancel</button>
            <button type="submit" disabled={saving} className="px-5 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default function VatRates() {
  const { userRoles } = useAuth()
  const canEdit = hasAnyRole(userRoles, ['ADMIN', 'OPERATIONS'])

  const [rows, setRows] = useState<VatRate[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [filterActive, setFilterActive] = useState<'all' | 'active' | 'inactive'>('active')
  const [modal, setModal] = useState(false)
  const [form, setForm] = useState<VatRate>(empty)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    let q = supabase.from('vat_rates').select('*').order('rate', { ascending: false })
    if (search) q = q.or(`code.ilike.%${search}%,description.ilike.%${search}%`)
    if (filterActive === 'active') q = q.eq('is_active', true)
    if (filterActive === 'inactive') q = q.eq('is_active', false)
    const { data } = await q
    setRows(data || [])
    setLoading(false)
  }, [search, filterActive])

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t) }, [load])

  const openAdd = () => { setForm(empty); setError(null); setModal(true) }
  const openEdit = (r: VatRate) => { setForm({ ...r }); setError(null); setModal(true) }
  const set = (k: keyof VatRate, v: unknown) => setForm(p => ({ ...p, [k]: v }))

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.code.trim() || !form.description.trim()) { setError('Code and Description are required.'); return }
    setSaving(true); setError(null)
    try {
      const payload = { code: form.code, description: form.description, rate: form.rate, is_active: form.is_active }
      if (form.id) {
        const { error: err } = await supabase.from('vat_rates').update(payload).eq('id', form.id)
        if (err) throw err
      } else {
        const { error: err } = await supabase.from('vat_rates').insert(payload)
        if (err) throw err
      }
      setModal(false); load()
    } catch (err: any) { setError(err?.message || err?.error_description || String(err) || 'Unknown error') }
    finally { setSaving(false) }
  }

  const toggleActive = async (r: VatRate) => {
    await supabase.from('vat_rates').update({ is_active: !r.is_active }).eq('id', r.id!)
    load()
  }

  const rateBadge = (rate: number) => {
    if (rate >= 20) return 'bg-red-100 text-red-700'
    if (rate > 0) return 'bg-yellow-100 text-yellow-700'
    return 'bg-green-100 text-green-700'
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="container mx-auto px-4 py-8">
        <div className="mb-6">
          <Link to="/data" className="text-sm text-blue-600 dark:text-blue-400 hover:underline">← Data Management</Link>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mt-1">VAT Rates</h1>
          <p className="text-gray-500 dark:text-gray-400 text-sm mt-0.5">UK VAT rate categories applied to charge codes and invoices</p>
        </div>

        <div className="flex flex-wrap gap-3 mb-4 items-end">
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search code or description…"
            className="flex-1 min-w-[200px] border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white" />
          <select value={filterActive} onChange={e => setFilterActive(e.target.value as typeof filterActive)}
            className="border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white">
            <option value="all">All</option>
            <option value="active">Active only</option>
            <option value="inactive">Inactive only</option>
          </select>
          {canEdit && (
            <button onClick={openAdd} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700">
              + Add VAT Rate
            </button>
          )}
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl shadow overflow-hidden">
          {loading ? (
            <div className="py-12 text-center text-gray-400">Loading…</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
                <tr>
                  {['Code', 'Description', 'Rate', 'Status', ...(canEdit ? ['Actions'] : [])].map(h => (
                    <th key={h} className="px-4 py-3 text-left font-semibold text-gray-600 dark:text-gray-300 text-xs uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {rows.map(r => (
                  <tr key={r.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <td className="px-4 py-3 font-mono font-bold text-gray-900 dark:text-white">{r.code}</td>
                    <td className="px-4 py-3 text-gray-700 dark:text-gray-200">{r.description}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${rateBadge(r.rate)}`}>
                        {Number(r.rate).toFixed(2)}%
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${r.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                        {r.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    {canEdit && (
                      <td className="px-4 py-3 space-x-3">
                        <button onClick={() => openEdit(r)} className="text-blue-600 hover:underline text-xs font-medium">Edit</button>
                        <button onClick={() => toggleActive(r)} className={`text-xs font-medium ${r.is_active ? 'text-red-500 hover:underline' : 'text-green-600 hover:underline'}`}>
                          {r.is_active ? 'Deactivate' : 'Activate'}
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr><td colSpan={canEdit ? 5 : 4} className="px-4 py-8 text-center text-gray-400">No VAT rates found</td></tr>
                )}
              </tbody>
            </table>
          )}
        </div>
        <div className="mt-2 text-xs text-gray-400">{rows.length} record{rows.length !== 1 ? 's' : ''}</div>
      </div>

      {modal && (
        <Modal title={form.id ? 'Edit VAT Rate' : 'Add VAT Rate'} onClose={() => setModal(false)} onSubmit={handleSubmit} saving={saving} error={error}>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Code" required>
              <input className={inputCls} value={form.code} onChange={e => set('code', e.target.value.toUpperCase())} placeholder="e.g. STANDARD" maxLength={20} />
            </Field>
            <Field label="Rate (%)" required>
              <input type="number" min="0" max="100" step="0.01" className={inputCls}
                value={form.rate} onChange={e => set('rate', parseFloat(e.target.value) || 0)} />
            </Field>
          </div>
          <Field label="Description" required>
            <input className={inputCls} value={form.description} onChange={e => set('description', e.target.value)} placeholder="Standard Rate" />
          </Field>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={form.is_active} onChange={e => set('is_active', e.target.checked)} className="h-4 w-4 text-blue-600 rounded" />
            <span className="text-sm text-gray-700 dark:text-gray-300">Active</span>
          </label>
        </Modal>
      )}
    </div>
  )
}
